import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Download, Printer } from 'lucide-react';
import { StudySession, studySessionsApi, authApi } from '../services/api';
import StudyMaterialRenderer from './StudyMaterialRenderer';
import { MixedText } from '../utils/rtl';
import { LoadingSpinner } from './ui/LoadingSpinner';
import ErrorMessage from './ui/ErrorMessage';

const StudySessionPdfExportView: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  // State
  const [session, setSession] = useState<StudySession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userName, setUserName] = useState<string>('');
  const [isVip, setIsVip] = useState(false);

  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (sessionId) {
      loadSessionData();
    }
  }, [sessionId]);

  // Inject print styles
  useEffect(() => {
    const printStyles = `
      @media print {
        body {
          margin: 0;
          padding: 0;
          background: white !important;
          font-family: 'Heebo', 'Times New Roman', serif !important;
        }
        
        /* Prevent blank page before content */
        body > * {
          page-break-before: avoid !important;
        }
        
        .print\\:hidden {
          display: none !important;
        }
        
        .print\\:p-6 {
          padding: 1.5rem !important;
        }
        
        .print\\:p-4 {
          padding: 1rem !important;
        }
        
        .print\\:p-3 {
          padding: 0.75rem !important;
        }
        
        .print\\:mb-8 {
          margin-bottom: 2rem !important;
        }
        
        .print\\:mb-6 {
          margin-bottom: 1.5rem !important;
        }
        
        .print\\:mb-4 {
          margin-bottom: 1rem !important;
        }
        
        .print\\:mb-3 {
          margin-bottom: 0.75rem !important;
        }
        
        .print\\:space-y-8 > * + * {
          margin-top: 2rem !important;
        }
        
        .print\\:space-y-2 > * + * {
          margin-top: 0.5rem !important;
        }
        
        .print\\:break-after-page {
          break-after: page !important;
        }
        
        .break-inside-avoid-page {
          break-inside: avoid !important;
        }
        
        @page {
          margin: 0.5cm;
          size: A4 portrait;
        }
        
        /* Ensure consistent page sizing */
        .page-content {
          min-height: auto !important;
          max-height: calc(297mm - 1cm) !important; /* A4 height minus margins */
        }
        
        /* Fix flexbox layout for print */
        .flex {
          display: flex !important;
        }
        
        .flex-col {
          flex-direction: column !important;
        }
        
        .flex-1 {
          flex: 1 !important;
        }
        
        /* Orange borders should print */
        .border-orange-500 {
          border-color: #f97316 !important;
        }
        
        .border-orange-300 {
          border-color: #fdba74 !important;
        }
        
        .bg-orange-500 {
          background-color: #f97316 !important;
        }
        
        .bg-orange-50 {
          background-color: #fff7ed !important;
        }
        
        .text-orange-600 {
          color: #ea580c !important;
        }
        
        /* Ensure gradient backgrounds print */
        .bg-gradient-to-r {
          background: linear-gradient(to right, #f97316, #ea580c) !important;
          -webkit-print-color-adjust: exact !important;
          color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        
        /* Force orange background to print */
        .from-orange-500 {
          background-color: #f97316 !important;
        }
        
        .to-orange-600 {
          background-color: #ea580c !important;
        }
        
        /* Ensure welcome page displays correctly */
        .welcome-page {
          page-break-after: always !important;
          height: calc(100vh - 1cm) !important; /* Account for page margins */
          max-height: 297mm !important; /* A4 height */
          display: flex !important;
          flex-direction: column !important;
          break-before: avoid !important;
          break-inside: avoid !important;
          overflow: hidden !important;
        }
        
        /* Override min-h-screen in print mode */
        .min-h-screen {
          min-height: auto !important;
        }
        
        /* Force white backgrounds */
        .bg-white {
          background-color: white !important;
        }
        
        /* Ensure shadows don't interfere with printing */
        .shadow-xl {
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1) !important;
        }
        
        /* Hide navbar and other app elements when printing */
        nav {
          display: none !important;
        }
        
        header {
          display: none !important;
        }
        
        /* Hide any element with navbar classes */
        .navbar, 
        [class*="navbar"],
        [class*="nav-"],
        .navigation {
          display: none !important;
        }
        
        /* Hide scroll bars and other UI elements */
        ::-webkit-scrollbar {
          display: none !important;
        }
      }
    `;

    const styleElement = document.createElement('style');
    styleElement.textContent = printStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const loadSessionData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load session and user data
      const [sessionData, userData] = await Promise.all([
        studySessionsApi.getSession(parseInt(sessionId!)),
        authApi.getCurrentUser()
      ]);

      setSession(sessionData);
      setUserName(userData.email?.split('@')[0] || 'StudyBel User');
      setIsVip(userData.is_vip);

      // Check VIP status
      if (!userData.is_vip) {
        setError(t('sessions:pdfExport.errors.vipRequired'));
        return;
      }

    } catch (err: any) {
      console.error('Error loading session data:', err);
      setError(t('errors:something_went_wrong'));
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = () => {
    // Trigger browser's print dialog which allows saving as PDF
    window.print();
  };

  const getMaterialTypeLabel = (materialType: string): string => {
    const labels: { [key: string]: string } = {
      'detailed_summary': isRTL ? 'סיכום השיעור' : 'Lesson Summary',
      'short_lesson_timeline': isRTL ? 'מהלך השיעור' : 'Lesson Timeline',
    };
    return labels[materialType] || materialType;
  };

  const getMaterialTypeIcon = (materialType: string): string => {
    const icons: { [key: string]: string } = {
      'detailed_summary': '📖',
      'short_lesson_timeline': '⏰',
    };
    return icons[materialType] || '📄';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" className="mx-auto mb-4" />
          <p className="text-gray-600 font-hebrew">
            <MixedText>{t('sessions:pdfExport.loading')}</MixedText>
          </p>
        </div>
      </div>
    );
  }

  if (error || !session || !session.study_materials || session.study_materials.length === 0) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center space-y-4">
          <ErrorMessage />
          <div className="space-x-4">
            <button
              onClick={() => navigate(-1)}
              className="text-orange-600 hover:text-orange-700 underline font-hebrew"
            >
              <MixedText>{t('common:actions.back')}</MixedText>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Sort materials to put detailed_summary first
  const sortedMaterials = [...session.study_materials].sort((a, b) => {
    if (a.material_type === 'detailed_summary') return -1;
    if (b.material_type === 'detailed_summary') return 1;
    return 0;
  });

  return (
    <>
      {/* Download Control - Hidden when printing, centered vertically */}
      <div className="fixed top-1/2 right-4 -translate-y-1/2 z-50 print:hidden">
        <button
          onClick={handleDownloadPdf}
          className="flex items-center gap-2 px-4 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg shadow-lg transition-all duration-200 hover:scale-105 font-hebrew"
        >
          <Download className="w-5 h-5" />
          <MixedText>{t('sessions:pdfExport.downloadPdf')}</MixedText>
        </button>
      </div>

      {/* PDF Content */}
      <div 
        ref={printRef}
        className="min-h-screen bg-white"
        style={{
          fontFamily: isRTL ? 'Heebo, sans-serif' : 'Times New Roman, serif',
          direction: isRTL ? 'rtl' : 'ltr'
        }}
      >
        {/* Header - Welcome Page */}
        <div className="welcome-page flex flex-col justify-between text-center p-6 print:p-4">
          {/* StudyBel Logo and Branding - Top section */}
          <div className="mb-8">
            <img 
              src="/logo_v2.png" 
              alt="StudyBel" 
              className="h-20 mx-auto mb-2"
            />
            <h1 className="text-4xl font-bold text-orange-600 mb-2">StudyBel</h1>
            <p className="text-lg text-gray-700 font-hebrew font-medium">
              <MixedText>העוזר AI שלך בלימודים</MixedText>
            </p>
          </div>

          {/* Session Title - Middle section */}
          <div className="my-8">
            <p className="text-base text-orange-600 font-hebrew font-medium mb-2">
              <MixedText>חומרי למידה שנוצרו בעזרת AI</MixedText>
            </p>
            <h2 className="text-3xl font-bold text-gray-900 mb-3 font-hebrew" dir={isRTL ? 'rtl' : 'ltr'}>
              <MixedText>{session.title || t('sessions:pdfExport.defaultTitle')}</MixedText>
            </h2>
            {session.course?.name && (
              <p className="text-xl text-gray-700 mt-2 font-hebrew">
                <MixedText>{session.course.name}</MixedText>
              </p>
            )}
            <div className="w-24 h-1 bg-orange-500 mx-auto mt-3"></div>
          </div>

          {/* Viral CTA - Bottom section */}
          <div className="mb-8">
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg px-8 py-6 shadow-lg max-w-sm mx-auto" style={{
              background: 'linear-gradient(to right, #f97316, #ea580c)',
              WebkitPrintColorAdjust: 'exact',
              colorAdjust: 'exact',
              printColorAdjust: 'exact'
            }}>
              <h3 className="text-xl font-bold font-hebrew mb-2">
                <MixedText>רוצים גם?</MixedText>
              </h3>
              <div className="text-lg font-bold font-mono bg-white text-orange-600 rounded py-1 px-3">
                study-bel.com
              </div>
            </div>
          </div>

        </div>
        {/* Page break after welcome page - outside the welcome page div */}

        {/* Content - A4 Pages */}
        <div className="w-full">
          {sortedMaterials.map((material, index) => (
            <div key={material.id} className="min-h-screen p-8 print:p-4 break-inside-avoid-page">
              {/* Material Header */}
              <div className="mb-6 print:mb-4">
                <div className="flex items-center justify-between border-b-2 border-orange-500 pb-3">
                  {isRTL ? (
                    <>
                      {/* In RTL: Title+Icon first (appears on right) */}
                      <div className="flex items-center gap-3" dir="rtl">
                        <span className="text-3xl">{getMaterialTypeIcon(material.material_type)}</span>
                        <h2 className="text-2xl font-bold text-gray-900 font-hebrew">
                          <MixedText>{getMaterialTypeLabel(material.material_type)}</MixedText>
                        </h2>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* In LTR: Title+Icon */}
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">{getMaterialTypeIcon(material.material_type)}</span>
                        <h2 className="text-2xl font-bold text-gray-900 font-hebrew">
                          <MixedText>{getMaterialTypeLabel(material.material_type)}</MixedText>
                        </h2>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Material Content */}
              <div className="mb-8 print:mb-6" dir={isRTL ? 'rtl' : 'ltr'}>
                <div className="bg-white border-2 border-orange-500 rounded-lg p-6 print:p-3">
                  <StudyMaterialRenderer
                    content={material.content}
                    materialType={material.material_type}
                    isRTL={isRTL}
                    showDevMarkdown={false}
                  />
                </div>
              </div>

              {/* Page break after each material except the last */}
              {index < sortedMaterials.length - 1 && (
                <div className="print:break-after-page"></div>
              )}
            </div>
          ))}

          {/* Questions Section if available */}
          {session.questions && session.questions.length > 0 && (
            <div className="min-h-screen p-8 print:p-4 break-inside-avoid-page">
              <div className="mb-6 print:mb-4">
                <div className="flex items-center justify-between border-b-2 border-orange-500 pb-3">
                  <div className="flex items-center gap-3" dir={isRTL ? 'rtl' : 'ltr'}>
                    <span className="text-3xl">❓</span>
                    <h2 className="text-2xl font-bold text-gray-900 font-hebrew">
                      <MixedText>{t('sessions:pdfExport.practiceQuestions')}</MixedText>
                    </h2>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                {session.questions.map((question, qIndex) => (
                  <div key={question.id} className="bg-white border-2 border-orange-300 rounded-lg p-4 print:p-3">
                    <div className="mb-3">
                      <span className="font-bold text-orange-600">
                        {t('common:labels.question')} {qIndex + 1}:
                      </span>
                    </div>
                    <StudyMaterialRenderer
                      content={question.question_text}
                      materialType="question"
                      isRTL={isRTL}
                      showDevMarkdown={false}
                    />
                    {question.explanation && (
                      <div className="mt-4 pt-4 border-t border-orange-200">
                        <div className="mb-2">
                          <span className="font-semibold text-green-600">
                            {t('sessions:pdfExport.answer')}:
                          </span>
                        </div>
                        <StudyMaterialRenderer
                          content={question.explanation}
                          materialType="explanation"
                          isRTL={isRTL}
                          showDevMarkdown={false}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t-2 border-orange-500 bg-white p-6 print:p-4 text-center">
          <div className="text-gray-600 text-sm font-hebrew">
            <MixedText>{`${t('sessions:pdfExport.footer')} - StudyBel ${new Date().getFullYear()}`}</MixedText>
          </div>
        </div>
      </div>
    </>
  );
};

export default StudySessionPdfExportView;